<template>
  <div class="mod-stock-stats">
    <el-row :gutter="20">
      <el-col :span="6">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>{{ $t('inventory.totalProducts') }}</span>
          </div>
          <div class="stat-item">
            <div class="stat-value">{{ stats.totalProducts }}</div>
            <div class="stat-label">{{ $t('inventory.products') }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>{{ $t('inventory.totalStockValue') }}</span>
          </div>
          <div class="stat-item">
            <div class="stat-value">¥{{ stats.totalValue }}</div>
            <div class="stat-label">{{ $t('inventory.stockValue') }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>{{ $t('inventory.lowStockAlert') }}</span>
          </div>
          <div class="stat-item">
            <div class="stat-value warning">{{ stats.lowStockCount }}</div>
            <div class="stat-label">{{ $t('inventory.products') }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>{{ $t('inventory.todayInbound') }}</span>
          </div>
          <div class="stat-item">
            <div class="stat-value success">{{ stats.todayInbound }}</div>
            <div class="stat-label">{{ $t('inventory.operations') }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>
    
    <el-row :gutter="20" style="margin-top: 20px;">
      <el-col :span="12">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>{{ $t('inventory.lowStockAlertList') }}</span>
            <el-button style="float: right; padding: 3px 0" type="text" @click="refreshLowStockAlerts">{{ $t('common.refresh') }}</el-button>
          </div>
          <el-table
            :data="lowStockAlerts"
            border
            v-loading="lowStockLoading"
            style="width: 100%;">
            <el-table-column
              prop="productName"
              header-align="center"
              align="center"
              :label="$t('inventory.productName')">
            </el-table-column>
            <el-table-column
              prop="currentStock"
              header-align="center"
              align="center"
              :label="$t('inventory.currentStock')">
              <template slot-scope="scope">
                <span class="text-danger">{{ scope.row.currentStock }}</span>
              </template>
            </el-table-column>
            <el-table-column
              prop="minStock"
              header-align="center"
              align="center"
              :label="$t('inventory.minStock')">
            </el-table-column>
            <el-table-column
              prop="alertLevel"
              header-align="center"
              align="center"
              :label="$t('inventory.alertLevel')">
              <template slot-scope="scope">
                <el-tag :type="scope.row.alertLevel === 'CRITICAL' ? 'danger' : 'warning'">
                  {{ scope.row.alertLevel === 'CRITICAL' ? $t('inventory.critical') : $t('inventory.warning') }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>{{ $t('inventory.stockTrend') }}</span>
            <el-button style="float: right; padding: 3px 0" type="text" @click="refreshTrendData">{{ $t('common.refresh') }}</el-button>
          </div>
          <div id="trendChart" style="height: 300px;"></div>
        </el-card>
      </el-col>
    </el-row>
    
    <el-row style="margin-top: 20px;">
      <el-col :span="24">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>{{ $t('inventory.stockDetailsStats') }}</span>
            <el-button style="float: right; padding: 3px 0" type="text" @click="refreshStockDetails">{{ $t('common.refresh') }}</el-button>
          </div>
          <el-table
            :data="stockDetails"
            border
            v-loading="stockDetailsLoading"
            style="width: 100%;">
            <el-table-column
              prop="productName"
              header-align="center"
              align="center"
              :label="$t('inventory.productName')">
            </el-table-column>
            <el-table-column
              prop="productCode"
              header-align="center"
              align="center"
              :label="$t('inventory.productCode')">
            </el-table-column>
            <el-table-column
              prop="category"
              header-align="center"
              align="center"
              :label="$t('product.category')">
            </el-table-column>
            <el-table-column
              prop="currentStock"
              header-align="center"
              align="center"
              :label="$t('inventory.currentStock')">
              <template slot-scope="scope">
                <span :class="getStockClass(scope.row.currentStock, scope.row.minStock)">
                  {{ scope.row.currentStock }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              prop="availableStock"
              header-align="center"
              align="center"
              :label="$t('inventory.availableStock')">
            </el-table-column>
            <el-table-column
              prop="lockedStock"
              header-align="center"
              align="center"
              :label="$t('inventory.lockedStock')">
            </el-table-column>
            <el-table-column
              prop="minStock"
              header-align="center"
              align="center"
              :label="$t('inventory.minStock')">
            </el-table-column>
            <el-table-column
              prop="maxStock"
              header-align="center"
              align="center"
              :label="$t('inventory.maxStock')">
            </el-table-column>
            <el-table-column
              prop="salePrice"
              header-align="center"
              align="center"
              :label="$t('inventory.salePrice')">
              <template slot-scope="scope">
                ¥{{ scope.row.salePrice }}
              </template>
            </el-table-column>
            <el-table-column
              prop="stockValue"
              header-align="center"
              align="center"
              :label="$t('inventory.stockValue')">
              <template slot-scope="scope">
                ¥{{ scope.row.stockValue }}
              </template>
            </el-table-column>
            <el-table-column
              prop="lastInboundTime"
              header-align="center"
              align="center"
              :label="$t('inventory.lastInboundTime')">
              <template slot-scope="scope">
                {{ scope.row.lastInboundTime | dateFormat }}
              </template>
            </el-table-column>
            <el-table-column
              prop="lastOutboundTime"
              header-align="center"
              align="center"
              :label="$t('inventory.lastOutboundTime')">
              <template slot-scope="scope">
                {{ scope.row.lastOutboundTime | dateFormat }}
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
  export default {
    data () {
      return {
        stats: {
          totalProducts: 0,
          totalValue: 0,
          lowStockCount: 0,
          todayInbound: 0
        },
        lowStockAlerts: [],
        lowStockLoading: false,
        stockDetails: [],
        stockDetailsLoading: false
      }
    },
    mounted () {
      this.getStats()
      this.getLowStockAlerts()
      this.getStockDetails()
      this.initTrendChart()
    },
    methods: {
      // 获取统计数据
      getStats () {
        this.$http({
          url: 'inventory/stats',
          method: 'get'
        }).then(({data}) => {
          if (data && data.code === 0) {
            this.stats = data.data
          }
        })
      },
      // 获取低库存预警
      getLowStockAlerts () {
        this.lowStockLoading = true
        this.$http({
          url: 'inventory/lowStockAlerts',
          method: 'get'
        }).then(({data}) => {
          if (data && data.code === 0) {
            this.lowStockAlerts = data.data
          }
          this.lowStockLoading = false
        })
      },
      // 获取库存明细
      getStockDetails () {
        this.stockDetailsLoading = true
        this.$http({
          url: 'inventory/stock/page',
          method: 'get',
          params: {
            'page': 1,
            'limit': 1000
          }
        }).then(({data}) => {
          if (data && data.code === 0) {
            this.stockDetails = data.data.list
          }
          this.stockDetailsLoading = false
        })
      },
      // 初始化趋势图
      initTrendChart () {
        // 这里可以使用 ECharts 或其他图表库
        // 暂时使用简单的模拟数据
        const chartData = {
          categories: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
          inbound: [120, 132, 101, 134, 90, 230, 210],
          outbound: [220, 182, 191, 234, 290, 330, 310]
        }
        
        // 实际项目中应该使用 ECharts
        console.log('趋势图数据:', chartData)
      },
      // 刷新低库存预警
      refreshLowStockAlerts () {
        this.getLowStockAlerts()
      },
      // 刷新趋势数据
      refreshTrendData () {
        this.initTrendChart()
      },
      // 刷新库存明细
      refreshStockDetails () {
        this.getStockDetails()
      },
      // 获取库存样式类
      getStockClass (currentStock, minStock) {
        if (currentStock <= minStock) {
          return 'text-danger'
        } else if (currentStock <= minStock * 1.5) {
          return 'text-warning'
        }
        return 'text-success'
      }
    }
  }
</script>

<style scoped>
  .box-card {
    margin-bottom: 20px;
  }
  .stat-item {
    text-align: center;
    padding: 20px 0;
  }
  .stat-value {
    font-size: 32px;
    font-weight: bold;
    color: #409EFF;
    margin-bottom: 10px;
  }
  .stat-value.warning {
    color: #E6A23C;
  }
  .stat-value.success {
    color: #67C23A;
  }
  .stat-label {
    font-size: 14px;
    color: #909399;
  }
  .text-danger {
    color: #f56c6c;
    font-weight: bold;
  }
  .text-warning {
    color: #e6a23c;
    font-weight: bold;
  }
  .text-success {
    color: #67c23a;
  }
</style>
